import type { TContentBlockBase, TLessonBase } from '@onesource/schemas';
import { ContentBlockResourceType, ContentBlockType } from '@onesource/schemas';
import type { ReactNode } from 'react';
import type { IconType } from 'react-icons';
import { LuClipboardList, LuFileText, LuNotebookText } from 'react-icons/lu';
import {
  MdCloudUpload,
  MdCode,
  MdLink,
  MdOutlineAssignment,
  MdOutlineQuiz,
  MdPictureAsPdf,
  MdPlayCircle,
} from 'react-icons/md';
import { RiPaletteLine } from 'react-icons/ri';
import { SiGoogledocs } from 'react-icons/si';

export interface LessonDetailViewProps {
  lesson: TLessonBase & { contentBlocks?: TContentBlockBase[] };
  progressPercent: number;
  breadcrumbs?: ReactNode;
  onNavigateToBlock: (block: TContentBlockBase) => void;
  onNavigateToSubLesson: (subLessonId: string) => void;
}

export interface CardProps {
  block: TContentBlockBase;
  onClick: () => void;
}

export const RESOURCE_TYPE_META: Record<ContentBlockResourceType, { icon: IconType; label: string }> = {
  [ContentBlockResourceType.VIDEO]: { icon: MdPlayCircle, label: 'Video' },
  [ContentBlockResourceType.PDF]: { icon: MdPictureAsPdf, label: 'Download' },
  [ContentBlockResourceType.TEXT]: { icon: LuFileText, label: 'Document' },
  [ContentBlockResourceType.DOCUMENT]: { icon: LuFileText, label: 'Document' },
  [ContentBlockResourceType.CANVA_TEMPLATE]: { icon: RiPaletteLine, label: 'Canva Template' },
  [ContentBlockResourceType.GOOGLE_DOC]: { icon: SiGoogledocs, label: 'Google Doc' },
  [ContentBlockResourceType.WORKSHEET]: { icon: LuClipboardList, label: 'Worksheet' },
  [ContentBlockResourceType.WORKBOOK]: { icon: LuNotebookText, label: 'Workbook' },
  [ContentBlockResourceType.QUIZ]: { icon: MdOutlineQuiz, label: 'Quiz' },
  [ContentBlockResourceType.REDIRECT]: { icon: MdLink, label: 'Link' },
  [ContentBlockResourceType.FORM]: { icon: MdOutlineAssignment, label: 'Form' },
  [ContentBlockResourceType.EMBED]: { icon: MdOutlineAssignment, label: 'Form' },
};

/**
 * What a block shows when the admin left "Resource type" empty — its block
 * type's own icon and wording. Only the types that reach the document list
 * need an entry; anything else falls through to Document.
 */
const BLOCK_TYPE_RESOURCE_META: Partial<Record<ContentBlockType, { icon: IconType; label: string }>> = {
  [ContentBlockType.PDF]: RESOURCE_TYPE_META[ContentBlockResourceType.PDF],
  [ContentBlockType.REDIRECT]: RESOURCE_TYPE_META[ContentBlockResourceType.REDIRECT],
  [ContentBlockType.FORM]: RESOURCE_TYPE_META[ContentBlockResourceType.FORM],
  [ContentBlockType.EMBED]: { icon: MdCode, label: 'Embed' },
  [ContentBlockType.GCS_UPLOAD]: { icon: MdCloudUpload, label: 'Upload' },
};

/**
 * Icon + label for one block: the resource type an admin tagged it with, else
 * its block type's default. Every lesson template renders through
 * `LessonDetailView`, and both the "In this lesson" summary and the document
 * cards read this one helper — so a block can never show one icon in the
 * summary and another on its card.
 */
export const blockResourceMeta = (
  block: Pick<TContentBlockBase, 'blockType' | 'resourceType'>,
): { icon: IconType; label: string } =>
  (block.resourceType ? RESOURCE_TYPE_META[block.resourceType] : undefined) ??
  BLOCK_TYPE_RESOURCE_META[block.blockType] ??
  RESOURCE_TYPE_META[ContentBlockResourceType.TEXT];
